'use client';

import { c as _c } from "react/compiler-runtime";
import { ExternalLink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { decodeHtmlEntities } from '@/lib/html-decode';
import { cn } from '@/lib/utils';

// Trailing debounce for heading extraction while wiki content streams in
const HEADING_EXTRACTION_DEBOUNCE_MS = 200;
type TOCItem = {
  id: string;
  title: string;
  level: number;
};
type WikiTableOfContentsProps = {
  content: string;
  className?: string;
  isPublicView?: boolean;
  owner?: string;
  repo?: string;
};
function watchWikiContent(extractHeadingsFromDOM: () => boolean) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let debounceTimeoutId: ReturnType<typeof setTimeout> | undefined;
  const contentObserver = new MutationObserver(() => {
    if (debounceTimeoutId) {
      clearTimeout(debounceTimeoutId);
    }
    debounceTimeoutId = setTimeout(() => {
      extractHeadingsFromDOM();
    }, HEADING_EXTRACTION_DEBOUNCE_MS);
  });
  const setupContentObserver = () => {
    const contentElement = document.getElementById('wiki-content');
    if (!contentElement) {
      return false;
    }

    // Extract headings immediately
    extractHeadingsFromDOM();

    // The documentObserver path and the polling fallback can both reach this
    // point; tear down any earlier observation before observing again.
    contentObserver.disconnect();

    // No characterData: heading additions/removals are childList mutations;
    // text-only edits inside an existing heading aren't a case this TOC
    // needs to track live.
    contentObserver.observe(contentElement, {
      childList: true,
      subtree: true
    });
    return true;
  };
  const documentObserver = new MutationObserver(() => {
    if (setupContentObserver()) {
      documentObserver.disconnect();
    }
  });

  // Try to set up observer immediately
  if (!setupContentObserver()) {
    // If wiki-content doesn't exist yet, watch for it to appear
    const mainContent = document.getElementById('wiki-main-content');
    if (mainContent) {
      documentObserver.observe(mainContent, {
        childList: true,
        subtree: true
      });
    }

    // Also try periodically as a fallback
    let attempts = 0;
    const trySetup = () => {
      if (attempts < 30) {
        // Increased attempts
        attempts++;
        if (setupContentObserver()) {
          documentObserver.disconnect();
        } else {
          timeoutId = setTimeout(trySetup, 200);
        }
      }
    };
    timeoutId = setTimeout(trySetup, 200);
  }
  return () => {
    contentObserver.disconnect();
    documentObserver.disconnect();
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (debounceTimeoutId) {
      clearTimeout(debounceTimeoutId);
    }
  };
}
export function WikiTableOfContents(t0) {
  const $ = _c(22);
  const {
    content,
    className,
    isPublicView: t1,
    owner,
    repo
  } = t0;
  const isPublicView = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [tocItems, setTocItems] = useState(t2);
  const [activeId, setActiveId] = useState("");
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      items: [],
      elements: []
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const lastExtractionRef = useRef(t3);
  let t4;
  let t5;
  if ($[2] !== content) {
    t4 = () => {
      if (!content) {
        lastExtractionRef.current = {
          items: [],
          elements: []
        };
        setTocItems([]);
        return;
      }
      const extractHeadingsFromDOM = () => {
        const contentElement = document.getElementById("wiki-content");
        if (!contentElement) {
          return false;
        }
        const headings = contentElement.querySelectorAll("h2, h3, h4, h5, h6");
        if (headings.length === 0) {
          return false;
        }
        const items = [];
        const itemElements = [];
        const usedIds = new Set();
        headings.forEach(heading => {
          const element = heading as HTMLElement;
          const title = element.textContent?.trim() || "";
          const level = Number.parseInt(element.tagName.substring(1), 10);
          if (title) {
            const baseId = title.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
            let id = baseId;
            let counter = 1;
            while (usedIds.has(id)) {
              id = `${baseId}-${counter}`;
              counter++;
            }
            usedIds.add(id);
            if (!element.id) {
              element.id = id;
            } else {
              id = element.id;
            }
            items.push({
              id,
              title,
              level
            });
            itemElements.push(element);
          }
        });
        const previous = lastExtractionRef.current;
        const isUnchanged = previous.items.length === items.length && previous.items.every((previousItem, index) => {
          const nextItem = items[index];
          return nextItem !== undefined && previousItem.id === nextItem.id && previousItem.title === nextItem.title && previousItem.level === nextItem.level && previous.elements[index] === itemElements[index];
        });
        if (!isUnchanged) {
          lastExtractionRef.current = {
            items,
            elements: itemElements
          };
          setTocItems(items);
        }
        return items.length > 0;
      };
      return watchWikiContent(extractHeadingsFromDOM);
    };
    t5 = [content];
    $[2] = content;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] !== tocItems) {
    t6 = () => {
      if (tocItems.length === 0) {
        return;
      }
      const mainContent = document.getElementById("wiki-main-content");
      if (!mainContent) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            setActiveId(entry.target.id);
          }
        });
      }, {
        root: mainContent,
        rootMargin: "-20% 0% -70% 0%",
        threshold: [0, 0.5, 1]
      });
      tocItems.forEach(item => {
        const element_0 = document.getElementById(item.id);
        if (element_0) {
          observer.observe(element_0);
        }
      });
      return () => observer.disconnect();
    };
    t7 = [tocItems];
    $[5] = tocItems;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = id_0 => {
      const element_1 = document.getElementById(id_0);
      if (!element_1) {
        return;
      }
      const mainContent_0 = document.getElementById("wiki-main-content");
      if (!mainContent_0) {
        return;
      }
      const elementRect = element_1.getBoundingClientRect();
      const mainRect = mainContent_0.getBoundingClientRect();
      const scrollTop = mainContent_0.scrollTop + elementRect.top - mainRect.top - 80;
      mainContent_0.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: "smooth"
      });
      setActiveId(id_0);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleClick = t8;
  let t9;
  if ($[9] !== className) {
    t9 = cn("py-6 space-y-4", className);
    $[9] = className;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isPublicView || $[12] !== owner || $[13] !== repo) {
    t10 = isPublicView && owner && repo && <a href={`https://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-2 rounded-md hover:bg-zinc-900 -ml-2"><ExternalLink className="h-4 w-4" />View on GitHub</a>;
    $[11] = isPublicView;
    $[12] = owner;
    $[13] = repo;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== activeId || $[16] !== tocItems) {
    t11 = tocItems.length > 0 && <><h4 className="text-base font-semibold text-foreground">On this page</h4><nav className="space-y-1">{tocItems.map(t12 => {
          const {
            id: id_1,
            title: title_0,
            level: level_0
          } = t12;
          const isActive = activeId === id_1;
          const isH3OrHigher = level_0 >= 3;
          return <button type="button" key={id_1} onClick={() => handleClick(id_1)} className={cn("cursor-pointer w-full text-left px-2 py-2 my-0.5 rounded-md transition-colors duration-200 ease", "hover:text-foreground hover:bg-zinc-900", isActive ? "text-foreground font-medium" : "text-muted-foreground", "text-sm", isH3OrHigher && "pl-6")}>{decodeHtmlEntities(title_0)}</button>;
        })}</nav></>;
    $[15] = activeId;
    $[16] = tocItems;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t9) {
    t12 = <div className={t9}>{t10}{t11}</div>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}